import React, { useMemo, useState, useEffect } from 'react'

import { Sparkles } from 'lucide-react'
import { DateTime } from 'luxon'
import {
	Bar,
	BarChart,
	CartesianGrid,
	XAxis,
	YAxis,
	ResponsiveContainer,
	Tooltip,
} from 'recharts'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Skeleton } from '@/shared/components/atoms/Skeleton'

interface OccupationData {
	since: string
	occupation: number
}

interface OccupationBarChartProps {
	occupationData?: OccupationData[]
	isLoading?: boolean
	integrationSource?: string | null
	integrationSourcePretty?: string | null
	showCard?: boolean
}

export const OccupationBarChart: React.FC<OccupationBarChartProps> = ({
	occupationData = [],
	isLoading = false,
	integrationSource,
	integrationSourcePretty,
	showCard = true,
}) => {
	const { t } = useTranslation('zones')
	const [hasImageError, setHasImageError] = useState(false)

	const partnerLogoUrl = useMemo(() => {
		if (!integrationSource) return null
		return new URL(
			`../../../assets/images/logos/partners/${integrationSource}.png`,
			import.meta.url
		).href
	}, [integrationSource])

	useEffect(() => {
		setHasImageError(false)
	}, [integrationSource])

	const chartData = occupationData.map((item) => ({
		time: DateTime.fromISO(item.since).toFormat('HH:mm'),
		occupation: item.occupation,
		fullDate: item.since,
	}))

	const skeletonData = useMemo(
		() =>
			Array.from({ length: 10 }, () => ({
				time: '',
				occupation: Math.random() * 60 + 20,
			})),
		[]
	)

	if (isLoading) {
		const loadingContent = (
			<>
				{showCard && (
					<div className="flex items-center gap-2 mb-3">
						<Skeleton className="h-8 w-8 rounded" />
						<Skeleton className="h-5 w-64" />
					</div>
				)}
				<ResponsiveContainer width="100%" height={280}>
					<BarChart
						data={skeletonData}
						margin={{ top: 10, right: 10, bottom: 30, left: -15 }}
					>
						<CartesianGrid
							strokeDasharray="3 3"
							vertical={false}
							stroke="var(--color-border)"
						/>
						<XAxis
							dataKey="time"
							angle={-45}
							textAnchor="end"
							height={60}
							interval="preserveStartEnd"
							tick={{ fill: 'var(--color-content-tertiary)', fontSize: 11 }}
							stroke="var(--color-border)"
						/>
						<YAxis
							domain={[0, 100]}
							ticks={[0, 20, 40, 60, 80, 100]}
							tick={{ fill: 'var(--color-content-tertiary)', fontSize: 11 }}
							stroke="var(--color-border)"
							tickMargin={5}
						/>
						<Bar
							dataKey="occupation"
							fill="var(--color-shade)"
							radius={[4, 4, 0, 0]}
							className="animate-pulse"
						/>
					</BarChart>
				</ResponsiveContainer>
			</>
		)
		return showCard ? (
			<div className="space-y-3">{loadingContent}</div>
		) : (
			loadingContent
		)
	}

	const hasIntegrationSourceImage = integrationSource && !hasImageError

	const content = (
		<>
			{showCard && (
				<div className="flex items-center gap-2 mb-3">
					{hasIntegrationSourceImage && (
						<div className="h-8 w-8 rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
							<img
								src={partnerLogoUrl!}
								alt={integrationSource}
								className="h-full w-full object-contain"
								width={32}
								height={32}
								onError={() => setHasImageError(true)}
							/>
						</div>
					)}
					{!hasIntegrationSourceImage && (
						<div className="h-8 w-8 bg-feedback-warning rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
							<Sparkles className="h-4 w-4" />
						</div>
					)}

					<div className="text-[14px] font-medium leading-[1.4]">
						{t('occupationChart.integratedWith', {
							source:
								integrationSourcePretty ||
								integrationSource ||
								t('occupationChart.integration'),
						})}
					</div>
				</div>
			)}
			<ResponsiveContainer width="100%" height={280}>
				<BarChart
					data={chartData}
					margin={{ top: 10, right: 10, bottom: 30, left: -15 }}
				>
					<CartesianGrid
						strokeDasharray="3 3"
						vertical={false}
						stroke="var(--color-border)"
					/>
					<XAxis
						dataKey="time"
						angle={-45}
						textAnchor="end"
						height={60}
						interval="preserveStartEnd"
						tick={{ fill: 'var(--color-content-tertiary)', fontSize: 11 }}
						stroke="var(--color-border)"
					/>
					<YAxis
						domain={[0, 100]}
						ticks={[0, 20, 40, 60, 80, 100]}
						tick={{ fill: 'var(--color-content-tertiary)', fontSize: 11 }}
						stroke="var(--color-border)"
						tickMargin={5}
					/>
					<Tooltip
						contentStyle={{
							backgroundColor: 'var(--color-popover)',
							border: '1px solid var(--color-border)',
							borderRadius: '8px',
							color: 'white',
						}}
						labelStyle={{ color: 'white', fontWeight: 500 }}
						formatter={(value: number) => [
							`${value}%`,
							t('occupationChart.occupation'),
						]}
					/>
					<Bar
						dataKey="occupation"
						fill="white"
						radius={[4, 4, 0, 0]}
						isAnimationActive={false}
					/>
				</BarChart>
			</ResponsiveContainer>
		</>
	)

	return showCard ? <div className="space-y-3">{content}</div> : content
}
